import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, Save, X, Calendar, MapPin, FileText,
  AlertCircle, CheckCircle, Settings, Building, Upload,
  Paperclip, Activity, Target, Clock, Award, BarChart3,
  TrendingUp, RefreshCw, Zap, Star, Eye, ArrowRight,
  Download, ExternalLink
} from 'lucide-react';
import { useLanguage } from '../StoreContext/LanguageContext';
import apiService from '../config';

// Utility: Convert image URL to File object for binary upload
const urlToFile = async (url, filename, mimeType) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new File([blob], filename, { type: mimeType });
};

// Utility: Detect MIME type from URL or filename
const detectMimeType = (url) => {
  const extension = url.split('.').pop().toLowerCase();
  const mimeTypes = {
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif'
  };
  return mimeTypes[extension] || 'application/octet-stream';
};

// Document Modal Component - Updated Version
const DocumentModal = ({ isOpen, onClose, url, fileName, label }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { language, translations } = useLanguage();


  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, url]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getFileExtension = (url) => {
    return url.split('.').pop().toLowerCase();
  };

  const isImage = (url) => {
    const ext = getFileExtension(url);
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  };

  const isPDF = (url) => {
    const ext = getFileExtension(url);
    return ext === 'pdf';
  };

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab if download fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Simple Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
      ></div>
      
      {/* Clean Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          
          {/* Simple Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center flex-1 min-w-0">
              <FileText className="h-5 w-5 text-gray-500 mr-3 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-medium text-gray-900 truncate">{label}</h3>
                <p className="text-sm text-gray-500 truncate">{fileName}</p>
              </div>
            </div>
            
            {/* Action Buttons - Only Download and Close */}
            <div className="flex items-center space-x-2 ml-4">
              <button
                onClick={handleDownload}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="Download"
              >
                <Download className="h-4 w-4 mr-1" />
                {translations[language]?.Download}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Content Area */}
          <div className="relative bg-gray-50" style={{ height: '70vh' }}>
            
            {/* Simple Loading */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-600">{translations[language]?.Loadingdocument}</p>
                </div>
              </div>
            )}
            
            {/* Simple Error State */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div className="text-center max-w-md p-6">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">{translations[language]?.Previewnotavailable}</h4>
                  <p className="text-sm text-gray-600 mb-6">
                    {translations[language]?.ThisdocumentcannotbepreviewedinthebrowserYoucandownloadittoview}
                  </p>
                  <div className="flex justify-center">
                    <button
                      onClick={handleDownload}
                      className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {translations[language]?.Download}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Document Content */}
            <div className="absolute inset-0">
              {isImage(url) && (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center p-4">
                  <img
                    src={url}
                    alt={fileName}
                    className="max-w-full max-h-full object-contain"
                    onLoad={handleLoad}
                    onError={handleError}
                    style={{ display: loading ? 'none' : 'block' }}
                  />
                </div>
              )}
              
              {isPDF(url) && (
                <iframe
                  src={`${url}#toolbar=1&navpanes=1&scrollbar=1`}
                  className="w-full h-full border-0"
                  onLoad={handleLoad}
                  onError={handleError}
                  style={{ display: loading ? 'none' : 'block' }}
                  title={fileName}
                />
              )}
              
              {!isImage(url) && !isPDF(url) && (
                <iframe
                  src={url}
                  className="w-full h-full border-0 bg-white"
                  onLoad={handleLoad}
                  onError={handleError}
                  style={{ display: loading ? 'none' : 'block' }}
                  title={fileName}
                />
              )}
            </div>
          </div>

          {/* Simple Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{translations[language]?.PressESCToClose}</span>
              <span className="capitalize">{getFileExtension(url)} {translations[language]?.File}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditProject = ({ onBack, setSubComponent, onUpdate }) => {
  const [projectData, setProjectData] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const [formData, setFormData] = useState({
    project_name: '',
    project_type: '',
    project_division: '',
    village_name: '',
    taluka_name: '',
    dist_name: '',
    land_acquisition_no: '',
    administrative_order_no: '',
    administrative_order_date: '',
    administrative: '',
    acquisition_proposal_no: '',
    acquisition_proposal_date: '',
    acquisition: '',
    no_of_budharak: '',
    full_name: '',
    stage_name: '',
    project_banner: '',
    total_budget: '',
    document: '',
    isactive: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [adminOrderFileName, setAdminOrderFileName] = useState('');
  const [acquisitionProposalFileName, setAcquisitionProposalFileName] = useState('');
  const [documentFileName, setDocumentFileName] = useState('');
  const [originalDocument, setOriginalDocument] = useState('');
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const { language, translations } = useLanguage();

  // ADD THESE NEW STATE VARIABLES:
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: '',
    message: '',
    type: 'success'
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    type: 'warning'
  });

  // New state variables to track original documents
  const [originalAdminDocument, setOriginalAdminDocument] = useState('');
  const [originalAcquisitionDocument, setOriginalAcquisitionDocument] = useState('');

  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    url: '',
    fileName: '',
    label: ''
  });

  // ADD THESE HELPER FUNCTIONS:
  const showAlert = (title, message, type = 'info') => {
    setAlertConfig({ title, message, type });
    setShowAlertModal(true);
    
    // Don't auto-hide success messages so user can see them
    if (type !== 'success') {
      setTimeout(() => setShowAlertModal(false), 5000);
    }
  };

  const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
    setConfirmConfig({ title, message, onConfirm, onCancel, type });
    setShowConfirmModal(true);
  };

  // Utility: Format date from API response to YYYY-MM-DD format
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Utility: Get file name from URL
  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  // Utility: Check if a filename/URL points to an image
  const isImageDocument = (nameOrUrl) => {
    if (!nameOrUrl) return false;
    return /\.(jpe?g|png|gif|bmp|webp)$/i.test(nameOrUrl);
  };

  // Close modal handler
  const closeModal = () => {
    // Clean up any object URLs to prevent memory leaks
    if (modalState.url && modalState.url.startsWith('blob:')) {
      URL.revokeObjectURL(modalState.url);
    }
    
    setModalState({
      isOpen: false,
      url: '',
      fileName: '',
      label: ''
    });
  };

  // Initialize form data when component mounts
  useEffect(() => {
    const storedData = sessionStorage.getItem('editProjectData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        console.log('Loaded project data:', parsedData);
        setProjectData(parsedData);

        // Initialize form with the loaded data
        setFormData({
          project_name: parsedData.project_name || '',
          project_type: parsedData.project_type || '',
          project_division: parsedData.project_division || '',
          village_name: parsedData.village_name || '',
          taluka_name: parsedData.taluka_name || '',
          dist_name: parsedData.dist_name || '',
          land_acquisition_no: parsedData.land_acquisition_no || '',
          administrative_order_no: parsedData.administrative_order_no || '',
          administrative_order_date: formatDateForInput(parsedData.administrative_order_date),
          administrative: parsedData.administrative,
          acquisition_proposal_no: parsedData.acquisition_proposal_no || '',
          acquisition_proposal_date: formatDateForInput(parsedData.acquisition_proposal_date),
          acquisition: parsedData.acquisition,
          no_of_budharak: parsedData.no_of_budharak || '',
          full_name: parsedData.full_name || '',
          stage_name: parsedData.stage_name || '',
          project_banner: parsedData.project_banner || '',
          total_budget: parsedData.total_budget || '',
          document: parsedData.documents1 || parsedData.document || '',
          isactive: parsedData.isactive !== undefined ? parsedData.isactive : true
        });

        // Store original documents separately
        if (parsedData.administrative) {
          setOriginalAdminDocument(parsedData.administrative);
          setAdminOrderFileName(getFileNameFromUrl(parsedData.administrative));
        }
        if (parsedData.acquisition) {
          setOriginalAcquisitionDocument(parsedData.acquisition);
          setAcquisitionProposalFileName(getFileNameFromUrl(parsedData.acquisition));
        }
        if (parsedData.documents1 || parsedData.document) {
          const docUrl = parsedData.documents1 || parsedData.document;
          setOriginalDocument(docUrl);
          setDocumentFileName(getFileNameFromUrl(docUrl));
          if (isImageDocument(docUrl)) {
            setDocumentPreviewUrl(docUrl);
          }
        }

        if (parsedData.dist_name) {
          setSelectedDistrict(parsedData.dist_name);
          setFormData(prev => ({
            ...prev,
            dist_name: parsedData.dist_name
          }));
        }

        if (parsedData.taluka_name) {
          setTimeout(() => {
            setSelectedTaluka(parsedData.taluka_name);
            setFormData(prev => ({
              ...prev,
              taluka_name: parsedData.taluka_name
            }));
          }, 300); // wait for taluka list to load
        }

        if (parsedData.village_name) {
          setTimeout(() => {
            setSelectedVillage(parsedData.village_name);
            setFormData(prev => ({
              ...prev,
              village_name: parsedData.village_name
            }));
          }, 600); // wait for village list to load
        }

        // Set preview image if banner exists
        if (parsedData.project_banner) {
          setPreviewImage(parsedData.project_banner);
        }

        // Clean up sessionStorage
        sessionStorage.removeItem('editProjectData');
      } catch (error) {
        console.error('Error parsing stored project data:', error);
        showAlert('Error', 'Failed to load project data', 'error');
      }
    }

    // Cleanup function to revoke object URLs on unmount
    return () => {
      if (modalState.url && modalState.url.startsWith('blob:')) {
        URL.revokeObjectURL(modalState.url);
      }
    };
  }, []);

  // Cleanup effect for modal URL changes
  useEffect(() => {
    return () => {
      if (modalState.url && modalState.url.startsWith('blob:')) {
        URL.revokeObjectURL(modalState.url);
      }
    };
  }, [modalState.url]);

  useEffect(() => {
    const fetchDistricts = async () => {
      setLoadingDistricts(true);
      try {
        // 🔥 API METHOD 2: Get all districts
        const response = await apiService.getDistricts();
        if (response && response.data) {
          setDistricts(response.data);
        }
      } catch (error) {
        console.error('Error fetching districts:', error);
        showAlert('Error', 'Failed to load districts. Please try again.', 'error');
      } finally {
        setLoadingDistricts(false);
      }
    };

    fetchDistricts();
  }, []);

  // Add this new useEffect to fetch talukas when district changes
  useEffect(() => {
    const fetchTalukas = async () => {
      if (selectedDistrict) {
        // Find the selected district object
        const districtObj = districts.find(d =>
        (d.name === selectedDistrict || d.district_name === selectedDistrict ||
          d.id === selectedDistrict || d.dist_no === selectedDistrict)
        );

        if (selectedDistrict) {
          setLoadingTalukas(true);
          try {
            // 🔥 API METHOD 3: Get talukas by district
            const response = await apiService.getTalukas(selectedDistrict);
            if (response && response.data) {
              setTalukas(response.data);
            }
          } catch (error) {
            console.error('Error fetching talukas:', error);
            showAlert('Error', 'Failed to load talukas. Please try again.', 'error');
          } finally {
            setLoadingTalukas(false);
          }
        }
      } else {
        setTalukas([]);
        setVillages([]);
        setSelectedTaluka('');
        setSelectedVillage('');
        setFormData(prev => ({
          ...prev,
          taluka_name: '',
          village_name: '',
          dist_name: '',
        }));
      }
    };

    fetchTalukas();
  }, [selectedDistrict, districts]);

  // Add this new useEffect to fetch villages when district and taluka change
  useEffect(() => {
    const fetchVillages = async () => {
      if (selectedDistrict && selectedTaluka) {
        const districtObj = districts.find(d =>
        (d.name === selectedDistrict || d.district_name === selectedDistrict ||
          d.id === selectedDistrict || d.dist_no === selectedDistrict)
        );

        const talukaObj = talukas.find(t =>
        (t.name === selectedTaluka || t.taluka_name === selectedTaluka ||
          t.eng_tehsil_name === selectedTaluka || t.id === selectedTaluka ||
          t.talukaNo === selectedTaluka || t.tehsil_no === selectedTaluka)
        );

        if (selectedDistrict && selectedTaluka) {
          setLoadingVillages(true);
          try {
            // 🔥 API METHOD 4: Get villages by district and taluka
            const response = await apiService.getVillages(
              selectedDistrict, selectedTaluka
            );
            if (response && response.data) {
              setVillages(response.data);
            }
          } catch (error) {
            console.error('Error fetching villages:', error);
            showAlert('Error', 'Failed to load villages. Please try again.', 'error');
          } finally {
            setLoadingVillages(false);
          }
        }
      } else {
        setVillages([]);
        setSelectedVillage('');
        setFormData(prev => ({
          ...prev,
          village_name: ''
        }));
      }
    };

    fetchVillages();
  }, [selectedDistrict, selectedTaluka, districts, talukas]);

  const handleDistrictChange = (e) => {
    const value = e.target.value;
    setSelectedDistrict(value);
    setSelectedTaluka('');
    setSelectedVillage('');

    // 🔥 CHANGE: Send district ID/number to backend instead of name
    setFormData(prev => ({
      ...prev,
      dist_name: value, // Send the ID/number directly
      taluka_name: '',
      village_name: ''
    }));
  };

  const handleTalukaChange = (e) => {
    const value = e.target.value;
    setSelectedTaluka(value);
    setSelectedVillage('');

    // Update form data
    const talukaObj = talukas.find(t => (t.talukaNo === value || t.tehsil_no === value || t.id === value));
    const talukaName = talukaObj ? talukaObj.eng_tehsil_name : value;

    setFormData(prev => ({
      ...prev,
      taluka_name: talukaName,
      village_name: '',
    }));
  };

  const handleVillageChange = (e) => {
    const value = e.target.value;
    setSelectedVillage(value);

    // Update form data
    const villageObj = villages.find(v => v.village_id === value);
    const villageName = villageObj ? villageObj.eng_village_name : value;

    setFormData(prev => ({
      ...prev,
      village_name: villageName
    }));
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showAlert('File Too Large', 'Please select a file smaller than 5MB', 'warning');
        e.target.value = '';
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        showAlert('Invalid File Type', 'Please select a valid image file (JPG, PNG, GIF)', 'warning');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        project_banner: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle administrative order file upload - UPDATED
  const handleAdminOrderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showAlert('File Too Large', 'Please select a file smaller than 10MB', 'warning');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        administrative: file
      }));
      // Update the filename to show the new file
      setAdminOrderFileName(file.name);
    }
  };

  // Handle acquisition proposal file upload - UPDATED
  const handleAcquisitionProposalFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showAlert('File Too Large', 'Please select a file smaller than 10MB', 'warning');
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        acquisition: file
      }));
      // Update the filename to show the new file
      setAcquisitionProposalFileName(file.name);
    }
  };

  // Handle project document upload
  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showAlert('File Too Large', 'Please select a file smaller than 10MB', 'warning');
      e.target.value = '';
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showAlert('Invalid File Type', 'Please select a PDF, DOC/DOCX, or image file', 'warning');
      e.target.value = '';
      return;
    }

    setFormData(prev => ({ ...prev, document: file }));
    setDocumentFileName(file.name);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setDocumentPreviewUrl(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setDocumentPreviewUrl(null);
    }
  };

  const handleRemoveDocument = () => {
    setFormData(prev => ({ ...prev, document: '' }));
    setDocumentFileName('');
    setDocumentPreviewUrl(null);
  };

  // Function to create preview URL for uploaded files
  const createFilePreviewUrl = (file) => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  // Function to handle viewing both existing and new files
  const handleViewDocument = (file, fileName, label, isNewFile = false) => {
    if (isNewFile && file instanceof File) {
      const previewUrl = createFilePreviewUrl(file);
      setModalState({
        isOpen: true,
        url: previewUrl,
        fileName: fileName,
        label: label
      });
    } else {
      setModalState({
        isOpen: true,
        url: file,
        fileName: fileName,
        label: label
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.project_name.trim()) {
      errors.project_name = 'Project name is required';
    }

    if (!formData.project_type.trim()) {
      errors.project_type = 'Project type is required';
    }

    if (!formData.project_division.trim()) {
      errors.project_division = 'Project division is required';
    }

    if (formData.no_of_budharak && isNaN(formData.no_of_budharak)) {
      errors.no_of_budharak = 'Number of Budharak must be a number';
    }

    // Validate total budget
    if (formData.total_budget && isNaN(formData.total_budget)) {
      errors.total_budget = 'Total budget must be a valid number';
    }

    if (formData.total_budget && parseFloat(formData.total_budget) < 0) {
      errors.total_budget = 'Total budget cannot be negative';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ENHANCED Handle form submission with confirmation
  const handleSubmit = async () => {
    if (!validateForm()) {
      showAlert('Validation Error', 'Please fix the form errors before submitting', 'warning');
      return;
    }

    // Show confirmation dialog
    showConfirm(
      'Confirm Update',
      'Are you sure you want to update this project? This action cannot be undone.',
      async () => {
        setLoading(true);
        setError(null);

        try {
          const newFormData = { ...formData };

          console.log('Form data being sent:', {
            ...newFormData,
            dist_name: typeof newFormData.dist_name,
            administrative: typeof newFormData.administrative,
            acquisition: typeof newFormData.acquisition
          });

          const response = await apiService.updateProject(projectData.project_id, newFormData);

          if (response.status === 200) {
            showAlert('Success', 'Project updated successfully!', 'success');
            setSuccess(true);
            setTimeout(() => {
              onUpdate?.();
              onBack();
            }, 1500);
          } else {
            throw new Error('Failed to update project');
          }
        } catch (error) {
          console.error('Error updating project:', error);
          showAlert('Update Failed', 'Failed to update project. Please try again.', 'error');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // ENHANCED Handle cancel with confirmation if there are changes
  const handleCancel = () => {
    // You could add logic here to check if form has been modified
    // and show confirmation dialog before canceling
    showConfirm(
      'Confirm Cancel',
      'Are you sure you want to cancel? Any unsaved changes will be lost.',
      () => {
        onBack();
      },
      null,
      'warning'
    );
  };

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-8 bg-green-50 rounded-xl border border-green-200">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-800 mb-2">{translations[language]?.Success}</h2>
          <p className="text-green-600">{translations[language]?.Projectupdatedsuccessfully}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Document Modal */}
      <DocumentModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        url={modalState.url}
        fileName={modalState.fileName}
        label={modalState.label}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        {/* <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => {
                  // Show confirmation before leaving if there are unsaved changes
                  showConfirm(
                    'Confirm Navigation',
                    'Are you sure you want to leave? Any unsaved changes will be lost.',
                    () => onBack(),
                    null,
                    'warning'
                  );
                }}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <ChevronLeft className="h-6 w-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-gradient-to-r from-blue-800 to-blue-600 bg-clip-text">
                  {translations[language]?.editProject || 'Edit Project'}
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">
                  {projectData ? `Project ID: ${projectData.project_id.toString().padStart(3, '0')}` : ''}
                </p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-7xl mx-auto">
          <div className="space-y-8">
            {/* Basic Information */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                  <Settings className="h-5 w-5 mr-2 text-blue-600" />
                  {translations[language]?.BasicInformation}
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations[language]?.ProjectName}
                    </label>
                    <input
                      type="text"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      placeholder={translations[language].EnterProjectName}
                      disabled
                    />
                    {validationErrors.project_name && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.project_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations[language]?.ProjectType}
                    </label>
                     <p className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600">
                      {formData.project_type || 'N/A'}
                    </p>
                    {validationErrors.project_type && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.project_type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations[language]?.ProjectDivision}
                    </label>
                    <select
                      name="project_division"
                      value={formData.project_division}
                      onChange={handleInputChange}
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    >
                      <option value="">{translations[language].SelectDivision}</option>
                      <option value="State Government/Union Territories">{translations[language].StateGovUT}s</option>
                      <option value="Public Works Division">{translations[language].PublicWorksDivision}</option>
                      <option value="Urban Development Division">{translations[language].UrbanDevDivision}</option>
                      <option value="Rural Development Division">{translations[language].RuralDevDivision}</option>
                      <option value="Industrial Development Division">{translations[language].IndustrialDevDivision}</option>
                      <option value="Ro">Ro</option>
                    </select>
                    {validationErrors.project_division && (
                      <p className="text-red-500 text-sm mt-1">{validationErrors.project_division}</p>
                    )}
                  </div>

                  {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {translations[language].CurrentStage}
                    </label>
                    <input
                      type="text"
                      value={formData.stage_name || 'Not Set'}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      placeholder="Current stage"
                    />
                    <p className="text-xs text-gray-500 mt-1">{translations[language].Thisfieldisnoteditable}</p>
                  </div> */}

                  <div className="">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                     {translations[language].CurrentStage}
                    </label>
                    <input
                      type="text"
                      value={formData.full_name || 'Not Assigned'}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      placeholder="Project incharge"
                    />
                    <p className="text-xs text-gray-500 mt-1">{translations[language].Thisfieldisnoteditable}</p>
                  </div>

                  {/* <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div className="p-3">
                      <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <Building className="h-5 w-5 mr-2 text-amber-600" />
                       {translations[language].ProjectStatus}
                      </h3>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isactive"
                          checked={formData.isactive}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-700">
                         {translations[language].Projectisactive}
                        </label>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              {/* Project Banner */}
              <div className="flex-1 min-w-[300px] bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <Upload className="h-5 w-5 mr-2 text-indigo-600" />
                    {translations[language].ProjectBanner}
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[language].UploadProjectBanner}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        {translations[language].Supportedformats}
                      </p>
                    </div>

                    {previewImage && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">{translations[language].Preview}:</p>
                        <div className="w-full max-w-md">
                          <img
                            src={previewImage}
                            alt="Project banner preview"
                            className="w-full h-48 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Document */}
              <div className="flex-1 min-w-[300px] bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <Paperclip className="h-5 w-5 mr-2 text-indigo-600" />
                    Project Document
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Upload Project Document
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={handleDocumentChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        PDF, DOC/DOCX or image, up to 10MB
                      </p>
                    </div>

                    {documentFileName && (
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => handleViewDocument(
                            formData.document instanceof File ? formData.document : originalDocument,
                            documentFileName,
                            'Project Document',
                            formData.document instanceof File
                          )}
                          className="flex items-center text-sm text-blue-600 hover:text-blue-800 truncate mr-3"
                        >
                          <FileText className="h-4 w-4 mr-1.5 flex-shrink-0" />
                          <span className="truncate">{documentFileName}</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveDocument}
                          className="text-sm text-red-500 hover:text-red-700 font-medium flex-shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    {documentPreviewUrl && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">{translations[language].Preview}:</p>
                        <div className="w-full max-w-md">
                          <img
                            src={documentPreviewUrl}
                            alt="Project document preview"
                            className="w-full h-48 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Land Information */}
              {/* <div className="w-full sm:w-[400px] bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-green-600" />
                    {translations[language].LandInformation}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                       {translations[language].TotalLandParcel}
                      </label>
                      <input
                        type="text"
                        name="land_acquisition_no"
                        value={formData.land_acquisition_no}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={translations[language].Enteracquisitionnumber}
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[language].NumberofLandOwner}
                      </label>
                      <input
                        type="number"
                        name="no_of_budharak"
                        value={formData.no_of_budharak}
                        onChange={handleInputChange}
                        min="0"
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          validationErrors.no_of_budharak ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder={translations[language].Enternumber}
                      />
                      {validationErrors.no_of_budharak && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.no_of_budharak}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Action Buttons */}
            <div className="">
              <div className="p-6">
                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                    disabled={loading}
                  >
                    <X className="h-4 w-4 inline mr-2" />
                    {translations[language].Cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline mr-2"></div>
                        {translations[language].Updating}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 inline mr-2" />
                        {translations[language].UpdateProject}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ADD THESE MODAL COMPONENTS: */}
      {/* Simple Alert Notification */}
      {showAlertModal && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm ${
            alertConfig.type === 'error' ? 'border-red-500' :
            alertConfig.type === 'warning' ? 'border-yellow-500' :
            alertConfig.type === 'info' ? 'border-blue-500' :
            'border-green-500'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  alertConfig.type === 'error' ? 'bg-red-100' :
                  alertConfig.type === 'warning' ? 'bg-yellow-100' :
                  alertConfig.type === 'info' ? 'bg-blue-100' :
                  'bg-green-100'
                }`}>
                  <span className={`font-bold ${
                    alertConfig.type === 'error' ? 'text-red-600' :
                    alertConfig.type === 'warning' ? 'text-yellow-600' :
                    alertConfig.type === 'info' ? 'text-blue-600' :
                    'text-green-600'
                  }`}>
                    {alertConfig.type === 'error' ? '✕' :
                     alertConfig.type === 'warning' ? '⚠' :
                     alertConfig.type === 'info' ? 'ℹ' : '✓'}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-gray-900">{alertConfig.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{alertConfig.message}</p>
              </div>
              <button
                onClick={() => setShowAlertModal(false)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
            <div className="p-6">
              <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${
                confirmConfig.type === 'danger' ? 'bg-red-100' :
                confirmConfig.type === 'warning' ? 'bg-yellow-100' :
                'bg-blue-100'
              }`}>
                <span className={`text-2xl ${
                  confirmConfig.type === 'danger' ? 'text-red-600' :
                  confirmConfig.type === 'warning' ? 'text-yellow-600' :
                  'text-blue-600'
                }`}>
                  {confirmConfig.type === 'danger' ? '⚠' :
                   confirmConfig.type === 'warning' ? '⚠' : '?'}
                </span>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                {confirmConfig.title}
              </h3>
              
              <p className="text-gray-600 text-sm mb-6 text-center">
                {confirmConfig.message}
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onCancel) confirmConfig.onCancel();
                  }}
                  className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  {translations[language].Cancel}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                  }}
                  className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${
                    confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                    confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {translations[language].Confirm}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EditProject;